import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/system";
import { CameraModalProps } from "../../../types/interfaces";
import { doc, setDoc } from "firebase/firestore";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { db } from "@/Utils/firebase";
import { useAuth } from "@/context/auth.context";
import { ScanUploader } from "@/services/ScanUploader";
import {
  clearAvatarSharingData,
  getAvatarSharingData,
} from "@/Utils/avatarSharingStorage";
import { logInfo } from "@/Utils/logger";
import { startNewJourney } from "@/telemetry/ids";

const StyledDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "20px",
    textAlign: "center",
    padding: "0px",
    width: "300px",
    backgroundColor: "#E3E6EB",
  },
});
const StyledDialogTitle = styled(DialogTitle)({
  fontWeight: "bold",
  fontSize: "18px",

  padding: "16px",
  backgroundColor: "#E3E6EB",
});
const StyledDialogContent = styled(DialogContent)({
  fontSize: "14px",
  padding: "0 24px 24px 24px",
  color: "#000",
  backgroundColor: "#E3E6EB",
});
const StyledDialogActions = styled(DialogActions)({
  display: "flex",
  justifyContent: "center",
  padding: "0",
  margin: "0",
  borderTop: "1px solid #ccc",
  backgroundColor: "#E3E6EB",
});
const ActionButton = styled(Button)({
  flex: 1,
  padding: "10px 0",
  margin: "0",
  border: "none",
  borderRadius: "0",
  fontWeight: "bold",
  color: "#007AFF",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "rgba(0, 122, 255, 0.1)",
  },
  "&:first-of-type": {
    borderRight: "1px solid #ccc",
  },
  "&:not(:first-of-type)": {
    marginLeft: "0",
  },
});
const DiscardModal: React.FC<CameraModalProps> = ({ open, handleClose }) => {
  const { navigateTo } = useCustomNavigation();
  const { user } = useAuth();
  const discard = async () => {
    const scanID = localStorage.getItem("scanID");
    // Capture the store URL BEFORE anything clears the sharing data — it decides
    // whether this discard also ends an active share and returns to the store.
    const { redirectUrl } = getAvatarSharingData();
    logInfo("[DiscardModal] User pressed discard", {
      scanID,
      userId: user?.uid,
      isSharing: Boolean(redirectUrl),
    });
    // The attempt ends here — the next scan is a new telemetry journey.
    startNewJourney();

    if (scanID) {
      // Mark the scan discarded so the server-side sweep removes its data (even
      // if the required scan data already finished uploading), and abort any
      // in-flight upload. Identical to discarding a scan taken WITHOUT a sharing
      // session — the discard logic is independent of the sharing connection.
      const docRef = doc(db, `upload_processing/${scanID}`);
      await setDoc(
        docRef,
        {
          userId: user?.uid,
          discarded: true,
        },
        { merge: true },
      );
      ScanUploader.getInstance().discardScan();
      localStorage.removeItem("scanID");
      localStorage.removeItem("timestamp");
    }

    handleClose();

    if (redirectUrl) {
      // Discarding during a share also cancels the share and returns the user to
      // the store. /share-cancelled owns the widget-session reset (progress
      // "IDLE"), the ttf-cancelled redirect, and clearing the sharing data — the
      // same machinery the "Yes, cancel" flow uses. `discarded` only changes the
      // copy shown there ("Avatar discarded" vs "Sharing cancelled").
      navigateTo("/share-cancelled", { state: { discarded: true } });
    } else {
      clearAvatarSharingData();
      navigateTo("/");
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <StyledDialogTitle id="alert-dialog-title">
        Discard New Avatar?
      </StyledDialogTitle>
      <StyledDialogContent>
        <DialogContentText id="alert-dialog-description">
          Any data from this session will be lost
        </DialogContentText>
      </StyledDialogContent>
      <StyledDialogActions>
        <ActionButton
          onClick={() => {
            discard();
          }}
        >
          Discard
        </ActionButton>
        <ActionButton onClick={handleClose}>Cancel</ActionButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};
export default DiscardModal;
